import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { addToConfig, getTotalPages } from './utils';
import { getProductByLocation } from '../helpers/store';
import ProductOptionsSelector from '../products/regular/subcomponents/ProductOptionsSelector';
import { MOBILE_L } from '../hooks/screen';

const ProductOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${MOBILE_L}px) {
    padding: 15px;
  }
`;

const ProductInfo = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h2 {
    font-size: 24px;
    margin-bottom: 10px;
    color: #333;

    @media (max-width: ${MOBILE_L}px) {
      font-size: 20px;
    }
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 5px;
  }
`;

const ProductImage = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: ${MOBILE_L}px) {
    width: 150px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
  width: 100%;
  justify-content: center;

  @media (max-width: ${MOBILE_L}px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  border: none;
  background: #479de3;
  color: white;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  min-width: 120px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #3885d3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: ${MOBILE_L}px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

const SkipButton = styled(ActionButton)`
  background: #e0e0e0;
  color: #333;

  &:hover {
    background: #d0d0d0;
  }
`;

const ProductOptions = ({
  shoot,
  productConfig,
  location,
  history,
  dispatch,
  additionalData,
}) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [skipTriggered, setSkipTriggered] = useState(false);

  // Get the current product from productConfig
  const currentIndex = parseInt(location.pathname.split('/').pop());

  // For You-Pick products, find the most recently configured product
  let product = null;
  let configItem = productConfig[currentIndex];

  if (productConfig[0]?.products?.length > 0 && currentIndex === 0) {
    // You-Pick flow: get the last product from the products array
    const products = productConfig[0].products;
    product = products[products.length - 1];
    configItem = productConfig[0];
  } else {
    // Regular flow: check the current index
    product = configItem?.product;
  }

  console.log('ProductOptions Debug:', {
    currentIndex,
    configItem,
    product,
    productConfig,
    pathname: location.pathname,
    productOptionSets: product?.product_option_sets,
    productOptions: product?.product_options,
    isYouPick: productConfig[0]?.products?.length > 0,
  });

  // Use effect to handle auto-skip to prevent infinite loops
  useEffect(() => {
    if (!product && !skipTriggered) {
      console.log('No product found, skipping to next step');
      setSkipTriggered(true);
      const pages = getTotalPages(shoot, additionalData);
      addToConfig({
        dispatch,
        history,
        productConfig,
        pages,
        itemToAdd: {},
        event: shoot,
      });
    }
  }, [
    product,
    skipTriggered,
    dispatch,
    history,
    productConfig,
    shoot,
    additionalData,
  ]);

  const handleOptionChange = (optionSetKey, optionKey) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionSetKey]: optionKey,
    }));
  };

  const handleContinue = () => {
    const pages = getTotalPages(shoot, additionalData);
    // Store the selected options with the product configuration
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: selectedOptions,
      event: shoot,
    });
  };

  const handleSkip = () => {
    const pages = getTotalPages(shoot, additionalData);
    // Continue without selecting options
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: {},
      event: shoot,
    });
  };

  // If no product, return null (useEffect will handle the skip)
  if (!product) {
    return null;
  }

  // Check if product has any option sets
  const hasOptions =
    product.product_option_sets?.length > 0 ||
    product.product_options?.length > 0;

  useEffect(() => {
    if (product && !hasOptions && !skipTriggered) {
      // Auto-skip if no options available
      console.log('Product has no options, auto-skipping');
      setSkipTriggered(true);
      handleSkip();
    }
  }, [product, hasOptions, skipTriggered]);

  return (
    <ProductOptionsWrapper>
      <ProductInfo>
        <h2>Configure Your Selection</h2>
        {product.image_thumbnail && (
          <ProductImage src={product.image_thumbnail} alt={product.name} />
        )}
        <p>
          <strong>{product.name}</strong>
        </p>
        <p>Please select your options below:</p>
      </ProductInfo>

      <ProductOptionsSelector
        product={product}
        selectedOptions={selectedOptions}
        onOptionChange={handleOptionChange}
      />

      <ButtonWrapper>
        <SkipButton onClick={handleSkip}>Skip Options</SkipButton>
        <ActionButton onClick={handleContinue}>Continue</ActionButton>
      </ButtonWrapper>
    </ProductOptionsWrapper>
  );
};

const mapStateToProps = (state) => ({
  shoot: state.shoot,
  productConfig: state.v3Order.productConfig,
  additionalData: state.additionalData,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductOptions),
);
